import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  ButtonToolbar,
  Checkbox,
  FlexboxGrid,
  Form,
  SelectPicker,
} from "rsuite";

import "./UserFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { RoleServices } from "../../../core/services/RoleServices";
import { get } from "lodash/fp";
import { UserListParams } from "./UserDataTypes";
import {
  genderObjArr,
  religionObjArr,
  statusObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
  tabViewSelected?: String;
}

export default function UserFilters({
  onSubmit,
  onClear,
  tabViewSelected,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    fname: "",
    lname: "",
    email: "",
    username: "",
    role: "",
    store: "",
    emp_code: "",
    designation: "",
    receive_notification: "",
    gender: "",
    religion: "",
    mobile: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    address_mobile: "",
    status: "",
  };
  const [formData, setFormData] = useState<UserListParams>(initSearchFormData);
  const [roleList, setRoleList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);
  const countryList = useSelector((state: any) => state.country.data);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  async function fetchRolesByTabView() {
    try {
      let params = { role_type: tabViewSelected };
      const roleRes = await RoleServices.roleList(params);
      setRoleList(roleRes.data.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    fetchRolesByTabView();
  }, [tabViewSelected]);

  async function handleCountryChange(countryId: number) {
    try {
      const stateRes = await HelperServices.getAllValidStates({
        country: countryId,
      });
      setStateList(get("data.data", stateRes));
      resetSelectionFor("state");
      handleFormChanges({ path: "country", value: countryId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleStateChange(stateId: number) {
    try {
      const res = await HelperServices.getAllValidCities({ state: stateId });
      setCityList(res.data.data);
      resetSelectionFor("city");
      handleFormChanges({ path: "state", value: stateId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleCityChange(cityId: number) {
    try {
      const res = await HelperServices.getAllValidAreas({ city: cityId });
      setAreaList(res.data.data);
      resetSelectionFor("area");
      handleFormChanges({ path: "city", value: cityId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleAreaChange(areaId: number) {
    try {
      const res = await HelperServices.getAllValidBuildings({ area: areaId });
      setBuildingList(res.data.data);
      resetSelectionFor("building");
      handleFormChanges({ path: "area", value: areaId });
    } catch (error) {
      console.log(error);
    }
  }

  function clearSearch() {
    resetSelectionFor("country");
    onClear();
    setFormData(initSearchFormData);
  }

  function resetSelectionFor(resetFor: string) {
    let resetData: Object = {};
    switch (resetFor) {
      case "country":
        resetData = {
          country: "",
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setStateList([]);
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "state":
        resetData = {
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "city":
        resetData = {
          city: "",
          area: "",
          building: "",
        };
        setAreaList([]);
        setBuildingList([]);
        break;
      case "area":
        resetData = {
          area: "",
          building: "",
        };
        setBuildingList([]);
        break;
      case "building":
        resetData = {
          building: "",
        };
        break;
    }
    let newFormData = {
      ...formData,
      ...resetData,
    };
    setFormData(newFormData);
  }

  const handleRecNotificationCheck = (value: any, checked: any) => {
    let form = updateIn(
      formData,
      ["receive_notification"],
      checked ? value : ""
    );
    setFormData(form);
  };

  return (
    <div>
      <div className="row mb-2">
        <ButtonToolbar>
          <Button onClick={handleSearchToggle}>{t("ADVANCE_SEARCH")}</Button>
        </ButtonToolbar>
      </div>
      {showFilter && (
        <div className="content mb-3 pb-0">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            layout="inline"
          >
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="fname">
                  <Form.ControlLabel>{t("FIRST_NAME")}</Form.ControlLabel>
                  <Form.Control name="fname" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="lname">
                  <Form.ControlLabel>{t("LAST_NAME")}</Form.ControlLabel>
                  <Form.Control name="lname" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="email">
                  <Form.ControlLabel>{t("EMAIL")}</Form.ControlLabel>
                  <Form.Control name="email" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="username">
                  <Form.ControlLabel>{t("USERNAME")}</Form.ControlLabel>
                  <Form.Control name="username" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="emp_code">
                  <Form.ControlLabel>{t("EMPLOYEE_CODE")}</Form.ControlLabel>
                  <Form.Control name="emp_code" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="designation">
                  <Form.ControlLabel>{t("DESIGNATION")}</Form.ControlLabel>
                  <Form.Control name="designation" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="mobile">
                  <Form.ControlLabel>{t("MOBILE")}</Form.ControlLabel>
                  <Form.Control name="mobile" type="number" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="pincode">
                  <Form.ControlLabel>{t("PINCODE")}</Form.ControlLabel>
                  <Form.Control name="pincode" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="status">
                  <Form.ControlLabel>{t("STATUS")}</Form.ControlLabel>
                  <SelectPicker
                    data={statusObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "status", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "status", value: "" });
                    }}
                    name="status"
                    value={formData?.status}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="role">
                  <Form.ControlLabel>{t("ROLE")}</Form.ControlLabel>
                  <SelectPicker
                    data={roleList}
                    valueKey="id"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "role", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "role", value: "" });
                    }}
                    name="role"
                    value={formData?.role}
                  />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="country">
                  <Form.ControlLabel>{t("Country")}</Form.ControlLabel>
                  <SelectPicker
                    data={countryList}
                    valueKey="id"
                    labelKey="country_name"
                    onSelect={(value, item) => {
                      handleCountryChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("country");
                    }}
                    name="country"
                    value={formData?.country}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="state">
                  <Form.ControlLabel>{t("STATE")}</Form.ControlLabel>
                  <SelectPicker
                    data={stateList}
                    valueKey="id"
                    labelKey="state_name"
                    onSelect={(value, item) => {
                      handleStateChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("state");
                    }}
                    name="state"
                    value={formData?.state}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="city">
                  <Form.ControlLabel>{t("CITY")}</Form.ControlLabel>
                  <SelectPicker
                    data={cityList}
                    valueKey="id"
                    labelKey="city_name"
                    onSelect={(value, item) => {
                      handleCityChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("city");
                    }}
                    name="city"
                    value={formData?.city}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="area">
                  <Form.ControlLabel>{t("AREA")}</Form.ControlLabel>
                  <SelectPicker
                    data={areaList}
                    valueKey="id"
                    labelKey="area_name"
                    onSelect={(value, item) => {
                      handleAreaChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("area");
                    }}
                    name="area"
                    value={formData?.area}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="building">
                  <Form.ControlLabel>{t("BUILDING")}</Form.ControlLabel>
                  <SelectPicker
                    data={buildingList}
                    valueKey="id"
                    labelKey="building_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "building", value });
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("building");
                    }}
                    name="building"
                    value={formData?.building}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="receive_notification">
                  <Form.ControlLabel>
                    {t("RECEIVE_NOTIFICATION")}
                  </Form.ControlLabel>
                  <Checkbox
                    name="receive_notification"
                    value={1}
                    onChange={handleRecNotificationCheck}
                    checked={formData.receive_notification == "" ? false : true}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="gender">
                  <Form.ControlLabel>{t("GENDER")}</Form.ControlLabel>
                  <SelectPicker
                    data={genderObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "gender", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "gender", value: "" });
                    }}
                    name="gender"
                    value={formData?.gender}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="religion">
                  <Form.ControlLabel>{t("RELIGION")}</Form.ControlLabel>
                  <SelectPicker
                    data={religionObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "religion", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "religion", value: "" });
                    }}
                    name="religion"
                    value={formData?.religion}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <div className="filter-btn">
              <Button onClick={() => clearSearch()}>{t("CLEAR_FILTER")}</Button>
              <Button onClick={() => onSubmit(formData)}>{t("Search")}</Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
